import { useEffect, useRef, useState } from "react";
import { ChevronDown, Quote } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { useT } from "@/i18n/LanguageContext";
import { useSiteMedia } from "@/lib/useSiteMedia";

const FOUNDER_PHOTO =
  "https://customer-assets.emergentagent.com/job_virtue-guide-demo/artifacts/unredlsu_IMG_2557.jpeg";

/**
 * Founder Story — premium expandable section.
 *
 * Founder portrait is sourced via the Site Media Registry
 * (slot `home.founder.portrait`) so the Content Vault is the single
 * source of truth. The hardcoded `FOUNDER_PHOTO` stays only as a
 * belt-and-suspenders fallback in case the registry hasn't answered
 * yet on first paint.
 */
export default function FounderSection() {
  const [open, setOpen] = useState(false);
  const sectionRef = useRef(null);
  const viewedRef = useRef(false);
  const { t } = useT();

  // Fire a one-time "founder_story_view" event when the section enters the viewport
  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !viewedRef.current) {
            viewedRef.current = true;
            trackEvent("founder_story_view", { page_path: "/" });
            obs.disconnect();
          }
        }
      },
      { threshold: 0.35 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    trackEvent(next ? "founder_story_expand" : "founder_story_collapse", {
      page_path: "/",
    });
  };

  return (
    <section
      id="founder"
      ref={sectionRef}
      data-testid="founder-section"
      className="relative py-20 md:py-32 lg:py-44 bg-[#070605] border-y border-white/5"
    >
      {/* ambient gold glow (clipped to section) */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-gold/[0.05] blur-[140px]" />
      </div>

      <div className="relative max-w-3xl mx-auto px-6 md:px-10 text-center">

        {/* eyebrow + subtitle */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <span className="h-px w-10 bg-gold" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-gold">{t("founder.eyebrow")}</span>
          <span className="h-px w-10 bg-gold" />
        </div>
        <h2
          data-testid="founder-headline"
          className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-bone tracking-tight leading-[1.05]"
        >
          {t("founder.headline1")} <span className="italic text-gold">StoicOS</span> {t("founder.headline2")}
        </h2>

        {/* PORTRAIT — prominent, centered */}
        <div className="mt-14 mb-10">
          <FounderPortrait />
        </div>

        {/* identity block */}
        <div data-testid="founder-identity" className="mt-2">
          <p data-testid="founder-name" className="font-serif text-3xl md:text-4xl text-bone tracking-tight">
            Ed Korporaal
          </p>
          <p className="mt-3 text-[11px] tracking-[0.28em] uppercase text-gold">{t("founder.role")}</p>
          <p className="mt-1 text-[11px] tracking-[0.28em] uppercase text-fog">{t("founder.tagline")}</p>
        </div>

        {/* PULL QUOTE 1 (visible by default) */}
        <figure data-testid="founder-quote-1" className="mt-12 mx-auto max-w-2xl relative">
          <Quote
            size={28}
            strokeWidth={1.25}
            color="#C5A059"
            className="absolute -top-4 left-1/2 -translate-x-1/2 opacity-70"
            aria-hidden
          />
          <blockquote className="font-serif italic text-2xl md:text-3xl lg:text-4xl leading-[1.2] text-bone pt-6">
            {t("founder.quote1")}
          </blockquote>
        </figure>

        {/* TOGGLE BUTTON */}
        <div className="mt-14">
          <button
            type="button"
            onClick={handleToggle}
            aria-expanded={open}
            aria-controls="founder-story-body"
            data-testid="founder-toggle"
            className="group inline-flex items-center gap-3 px-8 py-4 border border-gold/40 hover:border-gold bg-transparent hover:bg-gold/[0.04] transition-all duration-500 text-bone hover:text-gold"
          >
            <ChevronDown
              size={18}
              strokeWidth={1.5}
              color="#C5A059"
              className={`transition-transform duration-500 ${open ? "rotate-180" : ""}`}
            />
            <span className="text-[12px] tracking-[0.28em] uppercase">
              {open ? t("founder.toggleClose") : t("founder.toggleOpen")}
            </span>
          </button>
        </div>

        {/* EXPANDABLE BODY */}
        <div
          id="founder-story-body"
          data-testid="founder-story-body"
          aria-hidden={!open}
          className="grid transition-[grid-template-rows] duration-[900ms] ease-[cubic-bezier(.2,.7,.2,1)] mt-0"
          style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            <div
              className={`transition-opacity duration-700 ${open ? "opacity-100 delay-200" : "opacity-0"}`}
            >
              <StoryBody t={t} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------- subcomponents -------------------------- */

function FounderPortrait() {
  // Resolve the founder portrait through the Site Media Registry. Hook
  // lives here (rather than in FounderSection) because this is the
  // component that actually renders the <img>, keeping the scope
  // correct and the hook rules satisfied.
  const founderMedia = useSiteMedia("home.founder.portrait", {
    fallbackUrl: FOUNDER_PHOTO,
    fallbackAlt: "Ed Korporaal, Founder of StoicOS and creator of Virtue Guide",
  });
  return (
    <div className="relative mx-auto w-full max-w-md">
      {/* gold halo */}
      <div
        className="absolute -inset-6 bg-gradient-to-tr from-gold/0 via-gold/20 to-gold/0 blur-2xl opacity-70 pointer-events-none"
        aria-hidden
      />
      {/* gold inset frame */}
      <div className="relative p-[1px] bg-gradient-to-br from-gold/60 via-gold/10 to-gold/0">
        <div className="relative bg-[#050505] overflow-hidden">
          <img
            data-testid="founder-photo"
            src={founderMedia.url}
            alt={founderMedia.alt}
            loading="lazy"
            data-media-source={founderMedia.source || "legacy"}
            className="block w-full h-auto"
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{ boxShadow: "inset 0 0 80px 10px rgba(0,0,0,0.6)" }}
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
}

function StoryBody({ t }) {
  const cadence = [
    t("founder.cadence1"),
    t("founder.cadence2"),
    t("founder.cadence3"),
    t("founder.cadence4"),
  ];
  return (
    <div className="pt-16 md:pt-20 text-left">
      {/* divider after toggle */}
      <div className="gold-rule w-40 mx-auto mb-16" />

      <p className="text-center text-[11px] tracking-[0.3em] uppercase text-fog/70 mb-12">
        {t("founder.letter")}
      </p>

      {/* OPENING */}
      <div className="space-y-6 text-bone/85 font-light text-lg leading-[1.85] max-w-2xl mx-auto">
        <p>
          <span className="font-serif text-[2.5rem] sm:text-[3rem] md:text-[3.5rem] leading-none float-left mr-3 mt-1 text-gold">M</span>
          {t("founder.p1")}
        </p>
        <p>{t("founder.p2")}</p>
        <p className="font-serif italic text-bone/95 text-xl md:text-2xl leading-snug">
          {t("founder.p3")}
        </p>
        <p>{t("founder.p4")}</p>

        <div className="py-2">
          <p className="font-serif text-2xl md:text-3xl text-bone leading-tight">{t("founder.p5a")}</p>
          <p className="mt-2 font-serif text-2xl md:text-3xl text-gold italic leading-tight">{t("founder.p5b")}</p>
        </div>

        <p>{t("founder.p6")}</p>
        <p className="font-serif italic text-bone text-xl md:text-2xl leading-snug">
          {t("founder.p8")}
        </p>
        <p>{t("founder.p9")}</p>
      </div>

      {/* PULL QUOTE 2 */}
      <figure data-testid="founder-quote-2" className="my-14 mx-auto max-w-2xl relative px-8 md:px-10">
        <Quote
          size={32}
          strokeWidth={1.25}
          color="#C5A059"
          className="absolute -top-2 left-0"
          aria-hidden
        />
        <blockquote className="font-serif italic text-3xl md:text-4xl lg:text-[2.5rem] leading-[1.18] text-bone">
          {t("founder.quote2")}
        </blockquote>
      </figure>

      {/* CLOSING */}
      <div className="space-y-6 text-bone/85 font-light text-lg leading-[1.85] max-w-2xl mx-auto">
        <p>{t("founder.p10")}</p>
        <p className="font-serif text-xl md:text-2xl text-bone leading-snug">
          {t("founder.p11")}
        </p>

        <ul className="not-prose mt-2 space-y-2 list-none text-bone">
          {cadence.map((line) => (
            <li key={line} className="flex items-baseline gap-4 font-serif italic text-xl md:text-2xl">
              <span className="text-gold not-italic font-sans text-xs tracking-[0.3em]">·</span>
              {line}
            </li>
          ))}
        </ul>

        <p className="pt-2">{t("founder.p12")}</p>
        <p className="font-serif italic text-xl md:text-2xl text-bone leading-snug">{t("founder.p13")}</p>
        <p>{t("founder.p14")}</p>
      </div>

      {/* signature */}
      <div className="mt-12 max-w-2xl mx-auto flex items-center gap-4">
        <span className="h-px w-12 bg-gold" />
        <p className="font-serif italic text-xl text-gold">Ed Korporaal</p>
      </div>

      {/* closing tagline */}
      <div className="mt-20 text-center" data-testid="founder-closing">
        <div className="gold-rule mx-auto w-40 mb-10" />
        <p className="font-serif text-3xl md:text-5xl text-bone tracking-tight leading-[1.05]">{t("founder.closingMain")}</p>
        <p className="mt-3 font-serif italic text-2xl md:text-4xl text-gold tracking-tight leading-[1.05]">
          {t("founder.closingSub")}
        </p>
      </div>
    </div>
  );
}
